/**
 * CO-3 — when the leaderboard's live-attempts poll should slow down.
 *
 * A puzzle stays "active" in classroom state from the moment it's created
 * until the coach starts the next one, but every student's board actually
 * locks at `timer + 4s` (ActivePuzzle.tsx's own timeout). Polling at the fast
 * interval for the whole "active" window — including the long dead air after
 * everyone has finished — wastes requests. This computes the timestamp the
 * fast-poll window should end at: `timer seconds + 4s client expiry + 10s
 * grace` after the puzzle was first seen.
 *
 * `source === 'recording'` is replayable (a coach can rewind and relive the
 * same puzzleId), so its window is computed fresh on every call instead of
 * being cached — caching would wrongly skip the fast-poll window on a replay.
 *
 * For `source !== 'recording'` the end timestamp is cached per
 * `${meetingId}:${occurenceId}:${puzzleId}`, keyed on the first time this
 * puzzle is seen, so remounting the panel (e.g. switching right-hand tabs and
 * back) does not push the window out further.
 */

// Longest timer option offered for a classroom puzzle — the fallback when the
// duration on currentActivePuzzle is missing or not a usable number.
const DEFAULT_PUZZLE_SECONDS = 900;

// 4s client-side expiry (ActivePuzzle's own timeout) + 10s grace before we're
// confident every student's board has locked and the live attempts are final.
const EXPIRY_GRACE_SECONDS = 14;

const liveWindowEndCache = new Map<string, number>();

export interface GetPuzzlePollWindowEndInput {
  source: string;
  /** currentActivePuzzle.puzzleTime — used when source === 'recording'. */
  puzzleTime?: unknown;
  /** currentActivePuzzle.remainingTime — used otherwise. */
  remainingTime?: unknown;
  meetingId?: string | null;
  occurenceId?: string | null;
  puzzleId?: string | number | null;
  /** Injectable for tests; defaults to Date.now(). */
  now?: number;
}

function resolvePuzzleSeconds(
  source: string,
  puzzleTime: unknown,
  remainingTime: unknown,
): number {
  const raw = Number(source === 'recording' ? puzzleTime : remainingTime);
  return Number.isFinite(raw) && raw >= 0 ? raw : DEFAULT_PUZZLE_SECONDS;
}

export function getPuzzlePollWindowEnd({
  source,
  puzzleTime,
  remainingTime,
  meetingId,
  occurenceId,
  puzzleId,
  now = Date.now(),
}: GetPuzzlePollWindowEndInput): number {
  const seconds = resolvePuzzleSeconds(source, puzzleTime, remainingTime);
  const windowMs = (seconds + EXPIRY_GRACE_SECONDS) * 1000;

  if (source === 'recording') {
    return now + windowMs;
  }

  const key = `${meetingId ?? ''}:${occurenceId ?? ''}:${puzzleId ?? ''}`;
  const cached = liveWindowEndCache.get(key);
  if (cached != null) {
    return cached;
  }
  const end = now + windowMs;
  liveWindowEndCache.set(key, end);
  return end;
}

/** test-only: clears the live-window cache between unrelated test cases. */
export function resetPuzzlePollWindowCacheForTests(): void {
  liveWindowEndCache.clear();
}
